import React, { useMemo, useCallback, useContext } from 'react';
import { useParams } from 'react-router-dom';
import {
  parseAndFormatDate,
  formatCurrency,
  bytesFormat,
} from '@utils/formatters';
import { Col, Row, Select } from 'antd';
import { useTranslation } from 'react-i18next';
import useSwr from '@hooks/useSwr';
import ContractCard from '@components/ContractCard';
import RequestCard from '@components/RequestCard';
import ConsumptionCard from '@components/ConsumptionCard';
import { IObject } from '@utils/defaultTypes';
import InvoiceStats from '@components/InvoiceStandardDetails/InvoiceStats';
import SessionContext from '@providers/Session/SessionContext';
import CustomCard from '@components/CustomCard';
import InvoiceItemsMot from '../InvoiceItemsMot';

interface IDetailedInvoice {
  searchParams: IObject;
  setSearchParams: any;
}

const DetailedInvoice = ({
  searchParams,
  setSearchParams,
}: IDetailedInvoice) => {
  const { t } = useTranslation(['invoices', 'customers', 'requests']);
  const { customerId, invoiceId } = useParams();
  const { theme } = useContext(SessionContext);

  const { selectedSubInvoice } = useMemo(() => searchParams, [searchParams]);

  const { data } = useSwr(
    `/service-proxy/broker/${customerId}/invoices/${invoiceId}/details`,
  );

  const selectedInvoice = useMemo(() => {
    if (selectedSubInvoice) {
      const selected = data?.listaSubFaturas?.find(
        (item: { virtualAccountInfo: { idVirtualAccount: number } }) =>
          item?.virtualAccountInfo?.idVirtualAccount ===
          Number(selectedSubInvoice),
      );
      return {
        details: selected?.subFaturaInfo && {
          ...selected?.subFaturaInfo,
          nuQtdItens: selected?.nuQtdItens,
          opened: selected?.subFaturaInfo?.idStatusFatura === 'O',
        },
        contract: selected?.pricePlanInfo && {
          ...selected?.pricePlanInfo,
          name: selected?.virtualAccountInfo?.contractName,
          automaticBlocking: selected?.virtualAccountInfo?.blBloqueioAutomatico,
        },
        request: selected?.virtualAccountInfo,
        balance: selected?.dadosSaldo,
        listOfItems: selected?.listaItens,
      };
    }
    return undefined;
  }, [data?.listaSubFaturas, selectedSubInvoice]);

  const subInvoicesSelect = useMemo(
    () =>
      data?.listaSubFaturas?.map((subInvoice: any) => {
        const requestId = subInvoice?.virtualAccountInfo?.idVirtualAccount;
        const contractDescription = subInvoice?.pricePlanInfo?.dsPacote;

        return {
          label: `${requestId}${
            contractDescription && ` - ${contractDescription}`
          }`,
          value: requestId,
        };
      }),
    [data],
  );

  const consumption = useMemo(() => {
    const balance = selectedInvoice?.balance;

    return {
      totalFranchise: balance?.nuFranquiaTotal,
      franchiseConsumption: balance?.nuConsumoFranquia,
      surplusConsumption: balance?.nuConsumoExcedente,
      totalConsumption: balance?.nuConsumoTotal,
    };
  }, [selectedInvoice?.balance]);

  const invoicePeriodFormatted = useMemo(
    () =>
      selectedInvoice?.details?.dataInicio &&
      `${parseAndFormatDate(
        selectedInvoice?.details?.dataInicio,
        'dd-MM-yyyy',
        'dd/MM/yyyy',
      )} ${t('invoice.to')}
          ${parseAndFormatDate(
            selectedInvoice?.details?.dataTermino,
            'dd-MM-yyyy',
            'dd/MM/yyyy',
          )}`,
    [
      selectedInvoice?.details?.dataInicio,
      selectedInvoice?.details?.dataTermino,
      t,
    ],
  );

  const invoiceExpirationDateFormatted = useMemo(
    () =>
      parseAndFormatDate(
        selectedInvoice?.details?.dataVencimento,
        'dd-MM-yyyy',
        'dd/MM/yyyy',
      ),
    [selectedInvoice?.details?.dataVencimento],
  );

  const totalTrafficFormatted = useMemo(
    () =>
      selectedInvoice?.details?.nuTrafegoTotal &&
      bytesFormat(selectedInvoice?.details?.nuTrafegoTotal),
    [selectedInvoice?.details?.nuTrafegoTotal],
  );

  const terminalsFormatted = useMemo(
    () =>
      selectedInvoice?.details?.nuTerminais &&
      `${
        selectedInvoice?.details?.nuTerminais
          ? selectedInvoice?.details?.nuTerminais
          : ''
      }
      (${selectedInvoice?.details?.nuTerminaisUso} ${t('invoice.inUse')})`,
    [selectedInvoice?.details, t],
  );

  const handleSelectSubInvoice = useCallback(
    (value: number) => {
      setSearchParams({ selectedSubInvoice: value });
    },
    [setSearchParams],
  );

  const stats = useMemo(
    () => ({
      value:
        selectedInvoice?.details?.nuValorFatura &&
        formatCurrency(selectedInvoice?.details?.nuValorFatura),
      expirationDate: invoiceExpirationDateFormatted,
      totalTraffic: totalTrafficFormatted,
      terminals: terminalsFormatted,
      period: invoicePeriodFormatted,
      number: selectedInvoice?.details?.idFatura,
    }),
    [
      selectedInvoice?.details,
      invoiceExpirationDateFormatted,
      invoicePeriodFormatted,
      terminalsFormatted,
      totalTrafficFormatted,
    ],
  );

  return (
    <>
      <div style={{ maxWidth: 420, margin: '40px auto' }}>
        <span
          style={{
            fontSize: theme?.fontSizes?.small,
            color: theme?.colors?.text1,
          }}
        >
          {`${t('invoice.select-sub-invoice')}:`}
        </span>
        <Select
          optionFilterProp="label"
          options={subInvoicesSelect}
          value={selectedSubInvoice && Number(selectedSubInvoice)}
          loading={!subInvoicesSelect}
          onChange={handleSelectSubInvoice}
          disabled={!subInvoicesSelect || subInvoicesSelect?.length === 0}
          style={{ width: '100%' }}
          allowClear
          showSearch
        />
      </div>

      {!selectedSubInvoice ? (
        <div
          style={{
            textAlign: 'center',
          }}
        >
          <p
            style={{
              opacity: 0.6,
              maxWidth: 280,
              margin: '0 auto',
              color: theme?.colors?.text,
            }}
          >
            {t('selectASubInvoice')}
          </p>
        </div>
      ) : (
        <div>
          <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
            <Col lg={12} sm={24} xl={8} xs={24}>
              <CustomCard
                style={{ height: '100%' }}
                title={t('requests:cards.request')}
              >
                <RequestCard data={selectedInvoice?.request} />
              </CustomCard>
            </Col>
            <Col lg={12} sm={24} xl={16} xs={24}>
              <ContractCard
                data={selectedInvoice?.contract}
                virtualPlanId={selectedInvoice?.contract?.virtualPlanId}
              />
            </Col>
          </Row>

          <InvoiceStats data={stats} />

          <Row
            style={{
              margin: '16px 0',
            }}
          >
            <Col lg={24} md={24} xl={24} xs={24}>
              <ConsumptionCard data={consumption} />
            </Col>
          </Row>

          <InvoiceItemsMot
            data={selectedInvoice?.listOfItems}
            customerId={customerId}
            invoiceId={invoiceId}
          />
        </div>
      )}
    </>
  );
};

export default DetailedInvoice;
